import { useCallback, useMemo } from "react";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  UatProject,
  UatProjectCreate,
  UatProjectUpdate,
  UatProjectSync,
  UatProjectOrder,
} from "../lusca-search-api/generated-src";
import { UatProjectsApiFactory } from "../lusca-search-api/LuscaSearchApiFactory";

const QUERY_KEY = ["uat-projects"];

export default function useUatProjects() {
  const queryClient = useQueryClient();
  const api = useMemo(() => UatProjectsApiFactory(), []);

  const projectsQuery = useQuery<UatProject[], Error>(QUERY_KEY, async () => {
    const response = await api.listUatProjects(true, 100);
    return response.data.projects;
  });

  const projectOrdersQueries = useQueries({
    queries: (projectsQuery.data ?? []).map((project) => ({
      queryKey: [...QUERY_KEY, project.id, "orders"],
      queryFn: async (): Promise<UatProjectOrder[]> =>
        (await api.listUatProjectOrders(project.id, 100)).data.orders,
    })),
  });
  const projectOrders = (projectsQuery.data ?? []).reduce<
    Record<string, UatProjectOrder[]>
  >((ordersByProject, project, index) => {
    ordersByProject[project.id] = projectOrdersQueries[index]?.data ?? [];
    return ordersByProject;
  }, {});

  const createMutation = useMutation<UatProject, Error, UatProjectCreate>(
    async (project) => (await api.createUatProject(project)).data,
    {
      onSuccess: () => queryClient.invalidateQueries(QUERY_KEY),
    }
  );

  const updateMutation = useMutation<
    UatProject,
    Error,
    { projectId: string; update: UatProjectUpdate }
  >(
    async ({ projectId, update }) =>
      (await api.updateUatProject(projectId, update)).data,
    {
      onSuccess: () => queryClient.invalidateQueries(QUERY_KEY),
    }
  );

  const startSyncMutation = useMutation<UatProjectSync, Error, string>(
    async (projectId) => (await api.startUatProjectSync(projectId)).data,
    {
      onSuccess: () => queryClient.invalidateQueries(QUERY_KEY),
    }
  );

  const getSync = useCallback(
    async (projectId: string, syncId: string): Promise<UatProjectSync> =>
      (await api.getUatProjectSync(projectId, syncId)).data,
    [api]
  );

  const refresh = useCallback(
    () => queryClient.invalidateQueries(QUERY_KEY),
    [queryClient]
  );

  return {
    projects: projectsQuery.data ?? [],
    projectOrders,
    isLoading: projectsQuery.isLoading,
    error: projectsQuery.error,
    createProject: createMutation.mutateAsync,
    updateProject: (projectId: string, update: UatProjectUpdate) =>
      updateMutation.mutateAsync({ projectId, update }),
    startSync: startSyncMutation.mutateAsync,
    getSync,
    refresh,
    isCreating: createMutation.isLoading,
    isUpdating: updateMutation.isLoading,
    isStartingSync: startSyncMutation.isLoading,
    createError: createMutation.error,
    updateError: updateMutation.error,
    syncError: startSyncMutation.error,
  };
}
